import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function AlertsPage({ user, setUser }) {
  const [alerts, setAlerts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAlert, setNewAlert] = useState({
    asset_id: '',
    condition: 'ABOVE',
    price: ''
  });
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  const showAlertMessage = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm) => setConfirmModal({ show: true, message, onConfirm });

  useEffect(() => {
    fetchAlerts();
    fetchAssets();
  }, []);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await axios.get(`${API}/alerts`, config);
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Alerts fetch error:', err);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      setAssets(response.data);
    } catch (err) {
      console.error('Assets fetch error:', err);
    }
  };

  const createAlert = async () => {
    if (!newAlert.asset_id || !newAlert.price) {
      showAlertMessage('Lütfen tüm alanları doldurun', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      await axios.post(`${API}/alerts`, {
        ...newAlert,
        price: parseFloat(newAlert.price)
      }, config);
      
      showAlertMessage('✅ Uyarı oluşturuldu!', 'success');
      setShowCreateModal(false);
      setNewAlert({ asset_id: '', condition: 'ABOVE', price: '' });
      fetchAlerts();
    } catch (err) {
      showAlertMessage('❌ Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const deleteAlert = async (alertId) => {
    showConfirm('Bu uyarıyı silmek istediğinize emin misiniz?', async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        await axios.delete(`${API}/alerts/${alertId}`, config);
        showAlertMessage('✅ Uyarı silindi!', 'success');
        fetchAlerts();
      } catch (err) {
        showAlertMessage('❌ Hata: ' + (err.response?.data?.detail || err.message), 'error');
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">🔔 Fiyat Uyarıları</h1>
            <p className="text-gray-400 text-sm sm:text-base">Fiyat hedefleriniz için bildirim ayarlayın</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 hover:bg-purple-700 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-colors"
          >
            + Yeni Uyarı
          </button>
        </div>

        {/* Alerts List */}
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <div className="text-5xl mb-4">🔔</div>
              <div className="text-xl font-semibold mb-2">Henüz uyarı yok</div>
              <div className="text-gray-400 mb-4">Fiyat hedefleriniz için uyarı oluşturun</div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-600 hover:bg-purple-700 px-6 py-2 rounded-lg font-semibold"
              >
                Uyarı Oluştur
              </button>
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="bg-gray-800 rounded-lg p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl sm:text-2xl font-bold">{alert.asset_id}</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      alert.active ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {alert.active ? 'AKTİF' : 'PASİF'}
                    </span>
                  </div>
                  <div className="text-sm sm:text-base text-gray-400">
                    Fiyat <span className="font-semibold text-white">{alert.condition === 'ABOVE' ? 'üstüne çıkarsa' : 'altına düşerse'}</span>
                    {' '}<span className="font-bold text-purple-400">₺{alert.target_price.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Oluşturulma: {new Date(alert.created_at).toLocaleString('tr-TR')}
                  </div>
                </div>
                <button
                  onClick={() => deleteAlert(alert.id)}
                  className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-semibold text-sm transition-colors w-full sm:w-auto"
                >
                  Sil
                </button>
              </div>
            ))
          )}
        </div>

        {/* Create Alert Modal */}
        {showCreateModal && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-75 z-40" onClick={() => setShowCreateModal(false)} />
            <div className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 bg-gray-800 rounded-lg p-6 z-50 max-w-md w-full">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">Yeni Uyarı Oluştur</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Varlık</label>
                  <select
                    value={newAlert.asset_id}
                    onChange={(e) => setNewAlert({ ...newAlert, asset_id: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="">Varlık seçin</option>
                    {assets.map(asset => (
                      <option key={asset.id} value={asset.id}>{asset.symbol} - {asset.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Koşul</label>
                  <select
                    value={newAlert.condition}
                    onChange={(e) => setNewAlert({ ...newAlert, condition: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="ABOVE">Fiyat üstüne çıktığında</option>
                    <option value="BELOW">Fiyat altına düştüğünde</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Hedef Fiyat (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newAlert.price}
                    onChange={(e) => setNewAlert({ ...newAlert, price: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg font-semibold"
                >
                  İptal
                </button>
                <button
                  onClick={createAlert}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg font-semibold"
                >
                  Oluştur
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-sm mb-4 text-gray-300">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                  }}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AlertsPage;
